import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import EditableDatable from "./EditableDatatable";
import ErrorMessage from "./ErrorPopup";
import { companyDefault, salesquoteLines as SQL } from "../HelperFx/datalinks";
import { parseApiError } from "../HelperFx/common";

const REACT_DATA_URL = process.env.REACT_APP_API_DATA_URL;

const SaleQuoteLines = ({
  docNo,
  token,
  allowEdit = true,
  onLinesChanged,
}) => {
  const [lineData, setLineData] = useState([]);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const linesApi = `${REACT_DATA_URL}${SQL}`;

  const mapLine = (line) => ({
    ...line,
    systemId: line.id || line.systemId,
    systemSerialId: line.id || line.systemId,
    quantity: line.Quantity ?? line.quantity,
    uom: line.unitOfMeasure || line.uom || "",
  });

  const applyServerRow = (updated, rowId, serverRow) => {
    const mapped = mapLine(serverRow);
    const idx = updated.findIndex((row) => row.systemId === rowId);
    if (idx === -1) return;
    updated[idx] = {
      ...updated[idx],
      ...mapped,
      systemId: rowId,
      systemSerialId: mapped.systemId,
    };
  };

  const loadLines = useCallback(async () => {
    if (!docNo) return [];
    const response = await axios.get(
      `${linesApi}?${companyDefault}&$filter=documentnumber eq '${docNo}'`,
      { headers: { Authorization: `Basic ${token}` } },
    );
    const rows = (response.data?.value || []).map(mapLine);
    setLineData(rows);
    return rows;
  }, [docNo, linesApi, token]);

  useEffect(() => {
    loadLines().catch((err) => {
      setErrorMessage(parseApiError(err));
      setErrorOpen(true);
    });
  }, [loadLines]);

  const handlePageBlur = async (id, field, value, updated) => {
    const row = updated.find((r) => r.systemId === id);
    if (!row) return;

    const systemId = row.systemSerialId || row.id || row.systemId;
    if (!systemId || String(systemId).startsWith("empty")) return;

    try {
      const patchPayload =
        field === "quantity"
          ? { Quantity: Number(value) || 0 }
          : { [field]: value };

      const response = await axios.patch(
        `${linesApi}(${systemId})?${companyDefault}`,
        patchPayload,
        {
          headers: {
            Authorization: `Basic ${token}`,
            "Content-Type": "application/json",
            "If-Match": "*",
          },
        },
      );
      applyServerRow(updated, id, response.data);
      if (typeof onLinesChanged === "function") onLinesChanged();
    } catch (error) {
      throw new Error(parseApiError(error));
    }
  };

  const columns = useMemo(
    () => [
      {
        label: "Type",
        type: "text",
        name: "lineType",
        sortable: true,
        width: "120px",
        disabled: true,
      },
      {
        label: "Item No.",
        type: "text",
        name: "itemNo",
        sortable: true,
        width: "160px",
        disabled: true,
      },
      {
        label: "Description",
        type: "text",
        name: "description",
        sortable: true,
        width: "240px",
        disabled: true,
      },
      {
        label: "Quantity",
        type: "decimal",
        name: "quantity",
        sortable: true,
        width: "110px",
        disabled: !allowEdit,
      },
      {
        label: "UOM",
        type: "text",
        name: "uom",
        sortable: true,
        width: "90px",
        disabled: true,
      },
      {
        label: "Unit Price",
        type: "number",
        name: "unitPrice",
        format: "amount",
        sortable: true,
        width: "110px",
        disabled: true,
      },
      {
        label: "Line Amount",
        type: "number",
        name: "lineAmount",
        format: "amount",
        sortable: true,
        width: "120px",
        disabled: true,
      },
    ],
    [allowEdit],
  );

  return (
    <>
      <EditableDatable
        columns={columns}
        initialData={lineData}
        pageOnBlur={handlePageBlur}
        universalDisabled={!allowEdit}
        linesApi={linesApi}
        converteddocType="Quote"
        AllowDelete={false}
        onDataRefresh={loadLines}
        LineDescription="Purchase Quote Lines"
        SecondaryDescription="Quantity can be updated. Other line fields come from the confirmed quotation."
        authToken={token}
        allowEmptyRows={false}
      />
      <ErrorMessage
        isOpen={errorOpen}
        toggle={() => setErrorOpen(false)}
        caption="Error"
        message={errorMessage}
      />
    </>
  );
};

export default SaleQuoteLines;
